import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { deleteUser, listUsers, updateUserRole } from "../api/users";
import SignupForm from "../components/SignupForm";

export function SignupInApp() {
  const { profile } = useOutletContext();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [savingUserId, setSavingUserId] = useState(null);
  const [roleSelections, setRoleSelections] = useState({});
  const isAdmin = profile?.role === "admin";

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }

    setLoading(true);
    listUsers()
      .then((rows) => {
        setUsers(rows);
        setRoleSelections(Object.fromEntries(rows.map((user) => [user.id, user.role])));
      })
      .catch((err) => setError(err.message || "Unable to load users"))
      .finally(() => setLoading(false));
  }, [isAdmin]);

  const handleCreated = (user) => {
    setUsers((current) => [user, ...current.filter((item) => item.id !== user.id)]);
    setRoleSelections((current) => ({ ...current, [user.id]: user.role }));
  };

  const handleRoleUpdate = async (user) => {
    const role = roleSelections[user.id];
    if (!role || role === user.role) return;
    setError("");
    setNotice("");
    setSavingUserId(user.id);
    try {
      const updated = await updateUserRole(user.id, role);
      setUsers((current) => current.map((item) => item.id === updated.id ? updated : item));
      setRoleSelections((current) => ({ ...current, [updated.id]: updated.role }));
      setNotice(`${updated.email} role changed to ${updated.role}.`);
    } catch (err) {
      setError(err.message || "Unable to update user role");
      setRoleSelections((current) => ({ ...current, [user.id]: user.role }));
    } finally {
      setSavingUserId(null);
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Delete user ${user.email}? This removes their console access and cannot be undone.`)) {
      return;
    }

    setError("");
    setNotice("");
    setDeletingUserId(user.id);
    try {
      await deleteUser(user.id);
      setUsers((current) => current.filter((item) => item.id !== user.id));
    } catch (err) {
      setError(err.message || "Unable to delete user");
    } finally {
      setDeletingUserId(null);
    }
  };

  if (!profile) return <div className="spinner" />;

  if (!isAdmin) {
    return (
      <div className="alert alert-error">
        User management requires the admin role. Your role: <strong>{profile.role}</strong>
      </div>
    );
  }

  return (
    <div>
      <h2 className="page-title">User Management</h2>
      <p className="page-sub">
        Create technician accounts and review current console users.
      </p>
      {notice && <div className="alert alert-success">{notice}</div>}

      <div className="grid-2">
        <div className="card">
          <h2>Create User</h2>
          <p className="desc">New accounts default to the tech role.</p>
          <SignupForm onSuccess={handleCreated} />
        </div>

        <div className="card">
          <h2>Current Users</h2>
          <p className="desc">Accounts with access to this console.</p>

          {error && <div className="alert alert-error">{error}</div>}
          {loading ? (
            <div className="spinner" />
          ) : users.length === 0 ? (
            <div className="empty">No users found.</div>
          ) : (
            <div className="table-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td>{user.full_name || "-"}</td>
                      <td>{user.email}</td>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <select
                            className="input"
                            value={roleSelections[user.id] || user.role}
                            onChange={(event) => setRoleSelections((current) => ({ ...current, [user.id]: event.target.value }))}
                            disabled={user.id === profile.id || savingUserId === user.id}
                          >
                            <option value="viewer">Viewer</option>
                            <option value="tech">Technician</option>
                            <option value="admin">Admin</option>
                          </select>
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => handleRoleUpdate(user)}
                            disabled={user.id === profile.id || savingUserId === user.id || (roleSelections[user.id] || user.role) === user.role}
                          >
                            Save
                          </button>
                        </div>
                      </td>
                      <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                        <button
                          className="btn btn-outline btn-sm"
                          onClick={() => handleDelete(user)}
                          disabled={user.id === profile.id || deletingUserId === user.id}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
